import { useEffect, useRef, useState } from "react";
import { Badge, Box, Drawer, IconButton, Tooltip, Typography } from "@mui/material";
import SpeakerNotesOutlinedIcon from "@mui/icons-material/SpeakerNotesOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useComments } from "@/hooks/useComments";
import { RecordComments } from "@/components/common/comments/RecordComments";
import { countComments } from "@/components/common/comments/countComments";
import { ResourceName } from "@/types";

interface RecordCommentsButtonProps {
  /**
   * Subject record type — the REST **path segment** (`ResourceName`), typed so each embed
   * is compile-time checked. This is NOT the backend `ObjectType`/`subject_type` enum: the two
   * diverge for reimbursement (`ResourceName.REIMBURSEMENTS` = plural `"reimbursements"` vs the
   * enum's singular `"reimbursement"`). The URL uses the plural; `subject_type` is stamped
   * server-side, so passing the enum value here would silently 404.
   */
  subjectType: ResourceName;
  /** Subject record id. */
  subjectId: string | undefined;
}

const MIN_WIDTH = 360;
const MAX_WIDTH = 900;
const DEFAULT_WIDTH = 440;
const WIDTH_STORAGE_KEY = "coronis.commentsDrawerWidth";

const readStoredWidth = (): number => {
  const raw = Number(localStorage.getItem(WIDTH_STORAGE_KEY));
  return raw >= MIN_WIDTH && raw <= MAX_WIDTH ? raw : DEFAULT_WIDTH;
};

/**
 * Subject-agnostic header affordance for record comments (ADR-052 S4/S7): a comment
 * icon with a count badge that opens a right slide-over drawer holding the thread.
 * Chosen over a tab to avoid tab/header bloat. The count and the drawer share the
 * same useComments query (react-query dedupes), so the badge stays in sync with
 * adds/deletes.
 *
 * The drawer mirrors the shared SideDrawer used for the Tasks drawer (teal header,
 * close button) for visual consistency, lays its body out as a flex column so the
 * thread scrolls while the composer stays pinned, and is user-resizable via a
 * left-edge drag handle (width persisted to localStorage, clamped to a sane range).
 *
 * Parameterized on the subject so one component serves clients, policies, stop-loss
 * claims, and reimbursements (ADR-052 §8(f): new subjects are routes + UI only).
 */
export const RecordCommentsButton = ({ subjectType, subjectId }: RecordCommentsButtonProps) => {
  const [open, setOpen] = useState(false);
  const [width, setWidth] = useState<number>(readStoredWidth);
  const { comments } = useComments(subjectType, subjectId);
  // Total includes replies (shared with the task dialog header so the two agree).
  const count = countComments(comments);

  // Drag-to-resize. Window-level listeners (bound once) so the drag keeps tracking
  // even when the cursor outruns the thin handle; refs avoid stale closures.
  const resizingRef = useRef(false);
  const widthRef = useRef(width);
  widthRef.current = width;

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!resizingRef.current) return;
      // Drawer is anchored right, so its width is the distance from the cursor to the right edge.
      setWidth(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, window.innerWidth - e.clientX)));
    };
    const onUp = () => {
      if (!resizingRef.current) return;
      resizingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      try {
        localStorage.setItem(WIDTH_STORAGE_KEY, String(widthRef.current));
      } catch {
        /* localStorage unavailable — width is still applied for the session */
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    resizingRef.current = true;
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
  };

  if (!subjectId) return null;

  return (
    <>
      <Tooltip title="Comments">
        <IconButton onClick={() => setOpen(true)} size="small" aria-label="Comments">
          {/* Neutral count tucked onto the icon corner — a count, not an alert (and not a
              floating superscript): solid grey circle, overlaps the icon. */}
          <Badge
            badgeContent={count}
            max={99}
            overlap="circular"
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{
              "& .MuiBadge-badge": {
                bgcolor: "text.secondary",
                color: "background.paper",
                fontSize: "0.625rem",
                fontWeight: 600,
                height: 16,
                minWidth: 16,
                lineHeight: 1,
                p: 0,
              },
            }}
          >
            <SpeakerNotesOutlinedIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            position: "relative",
            width: { xs: "100vw", sm: width },
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          {/* Left-edge resize handle (pointer devices / sm+ only). */}
          <Box
            onMouseDown={startResize}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize comments panel"
            sx={{
              display: { xs: "none", sm: "block" },
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: 6,
              cursor: "ew-resize",
              zIndex: 2,
              "&:hover": { bgcolor: "primary.main", opacity: 0.4 },
            }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2.5,
              py: 1,
              bgcolor: "secondary.main",
              color: "background.paper",
              borderBottom: 1,
              borderColor: "divider",
              flexShrink: 0,
            }}
          >
            <Typography variant="h3" sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
              Comments
              {count > 0 && (
                <Typography component="span" variant="body2" sx={{ opacity: 0.8 }}>
                  {count}
                </Typography>
              )}
            </Typography>
            <IconButton
              onClick={() => setOpen(false)}
              size="small"
              aria-label="Close comments"
              sx={{ color: "background.paper", "&:hover": { bgcolor: "rgba(255,255,255,0.1)" } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Flex column body: RecordComments fills it (thread scrolls, composer pinned). */}
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", px: 2, py: 1.5 }}>
            <RecordComments subjectType={subjectType} subjectId={subjectId} />
          </Box>
        </Box>
      </Drawer>
    </>
  );
};
